
import React from 'react';
import { Guide } from '@/types/guides';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileText, Eye } from "lucide-react";
import { PdfPreview } from './PdfPreview';

interface GuideSidebarProps {
  guide: Guide;
  onDownload: (e: React.MouseEvent, guide: Guide) => void;
  onPreview: () => void;
}

export function GuideSidebar({ guide, onDownload, onPreview }: GuideSidebarProps) {
  return (
    <Card className="sticky top-24">
      <CardContent className="p-6">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-grandpal-green-dark mb-2">
            Guide Details
          </h3>
          <p className="font-medium text-grandpal-green-dark mb-3">
            Everything you need to choose the right nursing home in Ireland — confidently.
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {guide.categories?.map((category, index) => (
              <span
                key={index}
                className="text-xs px-3 py-1 bg-grandpal-green/10 text-grandpal-green-dark rounded-full"
              >
                {category}
              </span>
            ))}
          </div>
          <p className="text-grandpal-gray">
            This comprehensive 2025 guide is your trusted roadmap to finding the right care for an aging parent or loved one.
            Designed especially for families navigating decisions under pressure.
          </p>
        </div>

        {guide.preview_url && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-grandpal-green-dark mb-2 flex items-center">
              <Eye className="h-4 w-4 mr-1" />
              Quick Preview
            </h4>
            <div className="border rounded-lg overflow-hidden">
              <PdfPreview pdfUrl={guide.preview_url} maxPages={1} />
            </div>
            <p className="text-xs text-grandpal-gray mt-2">
              Preview only shows the first page. Download the guide for the full content.
            </p>
          </div>
        )}

        <div className="space-y-4">
          <Button 
            className="w-full bg-grandpal-green-dark hover:bg-grandpal-green-dark/90"
            onClick={(e) => guide && onDownload(e, guide)}
            disabled={!guide?.download_url}
          >
            <Download className="mr-2 h-5 w-5" />
            Download Guide
          </Button>
          <Button
            variant="outline"
            className="w-full border-grandpal-green text-grandpal-green-dark hover:bg-grandpal-green/10"
            onClick={onPreview}
          >
            <FileText className="mr-2 h-5 w-5" />
            Full Preview
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
